import React from 'react';
import { motion } from 'framer-motion';
import { useGame } from '../context/GameContext';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import SafeIcon from '../components/common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { GameService } from '../services/gameService';
import { formatNumber } from '../utils/formatters';
import { MENTORS } from '../data/mentors';

const { FiBox, FiUsers, FiTrendingUp, FiZap, FiSun } = FiIcons;

const VISUALS = {
  hero: "https://cdn.midjourney.com/25c40863-328f-48df-b6d6-d4d5ae06af89/0_0.webp",
  planter: "https://cdn.midjourney.com/1b01ec60-70b0-4e28-aaba-1f5fa9a70552/0_0.webp",
  gardener: "https://cdn.midjourney.com/ba06a9b3-cfde-4979-96f2-48f8ab4b5a17/0_0.webp"
};

export default function Production() {
  const { state, actions } = useGame();

  const planterCost = GameService.calculatePlanterCost(state.planters);
  const gardenerCost = GameService.calculateGardenerCost(state.gardeners);
  const canBuyPlanter = state.seeds >= planterCost;
  const canBuyGardener = state.seeds >= gardenerCost;
  const { planterRate, gardenerRate } = GameService.getProductionRates();

  const shopItems = [
    {
      id: 'planter',
      name: 'Planter',
      description: `Automated seeding units.`,
      rateDescription: `+${planterRate} seeds/sec`,
      icon: FiBox,
      cost: planterCost,
      owned: state.planters,
      canAfford: canBuyPlanter,
      action: actions.buyPlanter,
      image: VISUALS.planter,
      rate: planterRate,
    },
    {
      id: 'gardener',
      name: 'Gardener',
      description: `Skilled tenders for your grove.`,
      rateDescription: `+${gardenerRate} seeds/sec`,
      icon: FiUsers,
      cost: gardenerCost,
      owned: state.gardeners,
      canAfford: canBuyGardener,
      action: actions.buyGardener,
      image: VISUALS.gardener,
      rate: gardenerRate,
    }
  ];

  const totalSPS = GameService.calculateSPS(state.planters, state.gardeners, state.autoLevel, state.sps, state.compost);
  const itemSPS = GameService.calculateSPS(state.planters, state.gardeners, state.autoLevel, 0, 1);
  const mentorSPS = state.sps;
  const autoMultiplier = Math.pow(1.25, state.autoLevel - 1);

  const ownedUpgradableMentors = state.ownedCharacters
    .map(key => MENTORS[key])
    .filter(mentor => mentor && !mentor.isOwnedDefault && mentor.upgradeBaseCost);

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="relative h-64 rounded-3xl overflow-hidden shadow-2xl group">
        <div className="absolute inset-0 bg-black/30 z-10" />
        <img 
          src={VISUALS.hero} 
          alt="Production Hero" 
          className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" 
        />
        <div className="absolute inset-0 z-20 flex flex-col justify-end p-8">
          <h1 className="text-3xl lg:text-4xl font-cormorant font-bold text-white mb-2 text-shadow-sm">
            Production Hub
          </h1>
          <p className="text-white/90 font-dm-sans max-w-lg text-shadow-sm">
            Expand your automated infrastructure. Planters and Gardeners work tirelessly to grow your seed reserve.
          </p>
        </div>
      </div>

      {/* Equipment Section */}
      <div>
        <h2 className="text-2xl font-cormorant font-bold text-charcoal mb-6 flex items-center gap-3">
          <span className="w-8 h-8 rounded-full bg-sage/20 flex items-center justify-center text-sage">
            <SafeIcon icon={FiBox} className="w-4 h-4" />
          </span>
          Equipment
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {shopItems.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <Card className="flex flex-col h-full overflow-hidden p-0 border-0 shadow-lg group hover:shadow-xl transition-all duration-300">
                {/* Card Image Header */}
                <div className="relative h-48 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent z-10" />
                  <img 
                    src={item.image} 
                    alt={item.name} 
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
                  />
                  <div className="absolute bottom-4 left-4 z-20">
                    <h3 className="text-2xl font-cormorant font-bold text-white mb-1">{item.name}</h3>
                    <div className="flex items-center gap-2 text-white/90 text-sm font-dm-sans">
                      <span className="bg-white/20 backdrop-blur-md px-2 py-0.5 rounded-md border border-white/10">
                        Level {item.owned}
                      </span>
                      <span className="text-sage font-bold">{item.rateDescription}</span>
                    </div>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-6 flex flex-col flex-grow bg-white">
                  <p className="text-charcoal/70 text-sm mb-6 flex-grow">{item.description}</p>
                  
                  <div className="flex items-end justify-between gap-4">
                    <div>
                      <div className="text-xs text-charcoal/50 uppercase tracking-wider font-bold mb-1">Cost</div>
                      <div className="text-xl font-bold text-charcoal">{formatNumber(item.cost)}</div>
                    </div>
                    <Button 
                      variant={item.canAfford ? "primary" : "secondary"} 
                      disabled={!item.canAfford} 
                      onClick={item.action}
                      className="px-8 min-w-[120px]"
                    >
                      {item.canAfford ? 'Purchase' : 'Need Seeds'}
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Mentor Upgrades Section */}
      {ownedUpgradableMentors.length > 0 && (
        <div className="pt-4">
          <h2 className="text-2xl font-cormorant font-bold text-charcoal mb-6 flex items-center gap-3">
            <span className="w-8 h-8 rounded-full bg-goldenrod/20 flex items-center justify-center text-goldenrod">
              <SafeIcon icon={FiUsers} className="w-4 h-4" />
            </span>
            Mentor Empowerment
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {ownedUpgradableMentors.map((mentor, index) => {
              const level = state.mentorLevels?.[mentor.key] || 1;
              const cost = GameService.calculateMentorUpgradeCost(mentor, level);
              const canAfford = state.seeds >= cost;
              const currentSps = GameService.calculateMentorSpsContribution(mentor, level);
              const nextSps = GameService.calculateMentorSpsContribution(mentor, level + 1);

              return (
                <motion.div
                  key={mentor.key}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <Card className="flex flex-col h-full overflow-hidden p-0 border-0 shadow-lg group hover:shadow-xl transition-all duration-300">
                    <div className="relative h-40 overflow-hidden bg-charcoal">
                      <div className="absolute inset-0 bg-gradient-to-r from-charcoal/90 to-transparent z-10" />
                      {mentor.image && (
                        <img 
                          src={mentor.image} 
                          alt={mentor.name} 
                          className="w-full h-full object-cover opacity-80 group-hover:scale-105 transition-transform duration-700" 
                        />
                      )}
                      <div className="absolute inset-0 z-20 p-6 flex flex-col justify-center">
                        <h3 className="text-2xl font-cormorant font-bold text-white mb-1">{mentor.name}</h3>
                        <p className="text-white/70 text-sm font-dm-sans">{mentor.domain}</p>
                      </div>
                      <div className="absolute top-4 right-4 z-20 bg-black/40 backdrop-blur-md text-white text-xs px-3 py-1 rounded-full border border-white/10">
                        Lvl {level}
                      </div>
                    </div>

                    <div className="p-6 bg-white">
                      <div className="flex justify-between items-center mb-6 p-4 bg-sage/5 rounded-xl border border-sage/10">
                        <div className="text-center">
                          <div className="text-xs text-charcoal/50 uppercase font-bold">Current</div>
                          <div className="text-lg font-bold text-charcoal">{formatNumber(currentSps, 1)} SPS</div>
                        </div>
                        <SafeIcon icon={FiTrendingUp} className="text-sage w-5 h-5" />
                        <div className="text-center">
                          <div className="text-xs text-charcoal/50 uppercase font-bold">Next</div>
                          <div className="text-lg font-bold text-sage">{formatNumber(nextSps, 1)} SPS</div>
                        </div>
                      </div>

                      <div className="flex items-end justify-between gap-4">
                        <div>
                          <div className="text-xs text-charcoal/50 uppercase tracking-wider font-bold mb-1">Upgrade Cost</div>
                          <div className="text-xl font-bold text-charcoal">{formatNumber(cost)}</div>
                        </div>
                        <Button 
                          variant={canAfford ? "primary" : "secondary"} 
                          disabled={!canAfford} 
                          onClick={() => actions.upgradeMentor(mentor.key)}
                          className="px-6"
                        >
                          Upgrade
                        </Button>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}

      {/* Production Summary Footer */}
      <Card className="p-6 bg-gradient-to-br from-white to-sage/5 border-sage/20">
        <h2 className="text-xl font-cormorant font-bold text-charcoal mb-4 flex items-center gap-2">
          <SafeIcon icon={FiTrendingUp} className="w-5 h-5 text-charcoal/60" />
          Production Analytics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="p-4 bg-white rounded-xl border border-sage/10 shadow-sm">
            <p className="text-xs text-charcoal/50 uppercase tracking-wider font-bold mb-1">Equipment Base</p>
            <p className="text-lg font-bold text-charcoal">{formatNumber(itemSPS, 2)}</p>
          </div>
          <div className="p-4 bg-white rounded-xl border border-sage/10 shadow-sm">
            <p className="text-xs text-charcoal/50 uppercase tracking-wider font-bold mb-1">Mentor Base</p>
            <p className="text-lg font-bold text-charcoal">{formatNumber(mentorSPS, 2)}</p>
          </div>
          <div className="p-4 bg-white rounded-xl border border-sage/10 shadow-sm">
            <p className="text-xs text-charcoal/50 uppercase tracking-wider font-bold mb-1">Multipliers</p>
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold text-skyblue">{autoMultiplier.toFixed(2)}x</span>
              <span className="text-charcoal/40">×</span>
              <span className="text-lg font-bold text-goldenrod">{state.compost.toFixed(2)}x</span>
            </div>
          </div>
          <div className="p-4 bg-sage/10 rounded-xl border border-sage/20 shadow-sm">
            <p className="text-xs text-sage font-bold uppercase tracking-wider mb-1">Total Output</p>
            <p className="text-2xl font-bold text-charcoal">{formatNumber(totalSPS, 2)} <span className="text-sm font-normal text-charcoal/60">/s</span></p>
          </div>
        </div>
      </Card>
    </div>
  );
}